import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="relative pt-36 pb-24 lg:pt-44 lg:pb-36 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <motion.div 
          className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full filter blur-3xl"
          animate={{ 
            x: [0, 50, 0],
            y: [0, 30, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full filter blur-3xl"
          animate={{ 
            x: [0, -50, 0],
            y: [0, -30, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-full filter blur-3xl"
          animate={{ 
            x: [-100, 100, -100],
            y: [-50, 50, -50],
            scale: [1, 1.3, 1]
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(#1e3a8a 1px, transparent 1px), linear-gradient(to right, #1e3a8a 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-10">
            <motion.div 
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-5 py-2.5 rounded-full text-sm font-medium shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles size={16} />
              </motion.div>
              <span>Trusted IT Solutions Provider</span>
            </motion.div>

            <motion.h1 
              className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Innovative IT Solutions for{' '}
              <motion.span 
                className="bg-gradient-to-r from-blue-900 via-indigo-600 to-blue-600 bg-clip-text text-transparent"
                animate={{ 
                  backgroundPosition: ["0%", "100%", "0%"]
                }}
                transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                style={{ backgroundSize: "200% auto" }}
              >
                Business Growth
              </motion.span>
            </motion.h1>

            <motion.p 
              className="text-xl text-gray-600 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Custom software development, web & mobile applications, cloud services, and digital marketing solutions tailored for your business. Serving clients across India and abroad with reliable, scalable technology.
            </motion.p>

            {/* Key Benefits */}
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <motion.div 
                className="flex items-start space-x-3 p-4 rounded-xl bg-white/50 backdrop-blur-sm hover:bg-white/80 transition-all"
                whileHover={{ x: 5, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <CheckCircle2 className="text-blue-600 mt-1 flex-shrink-0" size={24} />
                </motion.div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">Transparent Pricing</h3>
                  <p className="text-sm text-gray-600">Clear quotations, no hidden costs</p>
                </div>
              </motion.div>
              <motion.div 
                className="flex items-start space-x-3 p-4 rounded-xl bg-white/50 backdrop-blur-sm hover:bg-white/80 transition-all"
                whileHover={{ x: 5, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                >
                  <CheckCircle2 className="text-blue-600 mt-1 flex-shrink-0" size={24} />
                </motion.div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">Dedicated Support</h3>
                  <p className="text-sm text-gray-600">Always available when you need us</p>
                </div>
              </motion.div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-5 pt-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <motion.div 
                whileHover={{ scale: 1.05, y: -2 }} 
                whileTap={{ scale: 0.95 }}
              >
                <Button 
                  onClick={() => scrollToSection('contact')}
                  size="lg"
                  className="bg-gradient-to-r from-blue-700 to-indigo-900 hover:from-blue-800 hover:to-indigo-950 text-white px-10 py-7 text-lg shadow-xl hover:shadow-2xl transition-all group"
                >
                  Get Started
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRight className="ml-2" size={20} />
                  </motion.div>
                </Button>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05, y: -2 }} 
                whileTap={{ scale: 0.95 }}
              >
                <Button 
                  onClick={() => scrollToSection('services')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-blue-900 text-blue-900 hover:bg-blue-50 px-10 py-7 text-lg"
                >
                  Our Services
                </Button>
              </motion.div>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div 
              className="flex items-center space-x-8 pt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1 }}
            >
              <motion.div 
                className="text-center"
                whileHover={{ scale: 1.1 }}
              >
                <motion.div 
                  className="text-4xl font-bold bg-gradient-to-r from-blue-900 to-indigo-600 bg-clip-text text-transparent"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 1.2 }}
                >
                  50+
                </motion.div>
                <div className="text-sm text-gray-600 font-medium">Projects Delivered</div>
              </motion.div>
              <div className="w-px h-16 bg-gray-300"></div>
              <motion.div 
                className="text-center"
                whileHover={{ scale: 1.1 }}
              >
                <motion.div 
                  className="text-4xl font-bold bg-gradient-to-r from-blue-900 to-indigo-600 bg-clip-text text-transparent"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 1.3 }}
                >
                  100%
                </motion.div>
                <div className="text-sm text-gray-600 font-medium">Client Satisfaction</div>
              </motion.div>
              <div className="w-px h-16 bg-gray-300"></div>
              <motion.div 
                className="text-center"
                whileHover={{ scale: 1.1 }}
              >
                <motion.div 
                  className="text-4xl font-bold bg-gradient-to-r from-blue-900 to-indigo-600 bg-clip-text text-transparent"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 1.4 }}
                >
                  24/7
                </motion.div>
                <div className="text-sm text-gray-600 font-medium">Support Available</div>
              </motion.div>
            </motion.div>
          </div>

          {/* Right Image */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <motion.div 
              className="relative rounded-2xl overflow-hidden shadow-2xl"
              whileHover={{ scale: 1.02, rotateY: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <motion.img
                src="https://images.unsplash.com/photo-1580982324076-d95230549339"
                alt="Professional IT Team"
                className="w-full h-auto object-cover"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.6 }}
              />
              {/* Overlay Card */}
              <motion.div 
                className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                whileHover={{ y: -5, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
              >
                <div className="flex items-center justify-between">
                  <motion.div whileHover={{ x: 5 }}>
                    <p className="text-sm text-gray-600 mb-1">Based in</p>
                    <p className="font-bold text-gray-900 text-lg">Calicut, Kerala</p>
                  </motion.div>
                  <motion.div 
                    className="text-right"
                    whileHover={{ x: -5 }}
                  >
                    <p className="text-sm text-gray-600 mb-1">Serving</p>
                    <p className="font-bold text-gray-900 text-lg">India & Abroad</p>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>

            {/* Floating Elements */}
            <motion.div 
              className="absolute -top-8 -right-8 w-28 h-28 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full opacity-20"
              animate={{ 
                scale: [1, 1.2, 1],
                rotate: [0, 90, 0]
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div 
              className="absolute -bottom-8 -left-8 w-36 h-36 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full opacity-20"
              animate={{ 
                scale: [1, 1.3, 1],
                rotate: [0, -90, 0]
              }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div 
              className="absolute top-1/2 -right-4 w-20 h-20 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-full opacity-20"
              animate={{ 
                scale: [1, 1.4, 1],
                y: [-20, 20, -20]
              }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
